<template>
  <Modal
    ref="addCatalogModalRef"
    content-id="catalog-product"
    :clickToClose="false"
    :showIcon="false"
    :modalStyle="{ maxWidth: 'calc(100vw - 32px)', width: '1328px' }"
    :style="catalogModalStyle"
    :outsideScrollDisabled="true"
    @modalClosed="addProductModalClosed"
  >
    <template v-slot:body>
      <div class="catalog-product-modal">
        <CatalogProductCard
          v-if="clickedProductModalImagesTransform?.length"
          :attributes="selectedProduct.attributes"
          :comissionRate="selectedProduct.commission"
          toolTipText="Komisyon oranı; mağazanıza, ürün kategorisine ve katıldığınız kampanyalara göre değişiklik gösterir."
          :images="clickedProductModalImagesTransform"
          :style="{ marginBottom: '24px' }"
        />
        <div class="catalog-product-main-wrapper">
          <h2 class="modal-product-title" @click="goToProductPage">
            {{ selectedProduct.productTitle }}
          </h2>
          <AddCatalogModalSelectableArea
            ref="addCatalogModalSelectableAreaRef"
            :isClickOpenSaleProductBtn="isClickOpenSaleProductBtn"
            :clickedProductModalData="clickedProductModalData"
            :hasClickedProductVariants="hasClickedProductVariants"
            @selectedShipmentTemplate="selectedShipmentTemplate = $event"
            @selectedVariants="selectedVariants = $event"
          />
          <AddCatalogModalBulkOperations
            v-show="hasClickedProductVariants"
            ref="bulkOperationsRef"
            :selectedRowsLength="getSelectedRowsLength"
            :categoryId="selectedProductCategoryId"
            @applySelected="applySelected"
          />
          <Table
            ref="catalogTableRef"
            :variants="selectedVariants"
            :columns="columnsCatalogTable"
            :hasClickedProductVariants="hasClickedProductVariants"
          />
          <div class="modal-bottom">
            <div class="modal-bottom-alert">
              <img src="@/assets/images/icons/info-circle.svg" />
              <p class="modal-bottom-text">
                Ürüne açıklama veya farklı detay ekleme işlemlerini, satışa
                açtıktan sonra <b>Ürün Yönetimi’nden</b> yapabilirsiniz.
              </p>
            </div>
            <Button
              primary
              lg
              style="width: auto"
              :disabled="isOpenSaleProductBtnDisabled"
              @click="clickedOpenSaleProductBtn"
            >
              Ürünü Satışa Aç
            </Button>
          </div>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { useStore } from "vuex";
import { Button, Modal, CatalogProductCard } from "@n11/so-ui-components";
import { columnService } from "../services/getCatalogColumns";
import { sendOpenProductForSaleEvent } from "@/utils/ga4/helper";
import { sendProductLinkClickEvent } from "@/utils/ga4/helper";
import { HOST_NAMES } from "@/utils/enum/enviromentHosts";
import AddCatalogModalSelectableArea from "./AddCatalogModalSelectableArea.vue";
import AddCatalogModalBulkOperations from "./AddCatalogModalBulkOperations.vue";
import Table from "./TableForCatalog";

const emit = defineEmits([
  "loadingClickedProductId",
  "setIsNotificationShow",
  "openListedProductWarningModal",
]);

const props = defineProps({
  paginatedRows: {
    type: Object,
    required: true,
  },
});

const store = useStore();
const columnServiceInit = columnService();
const validate = columnServiceInit.validate;
const columnsCatalogTable = columnServiceInit.columns;

const addCatalogModalRef = ref();
const catalogModalStyle = ref();
const clickedProductModalImages = ref([]);
const selectedProduct = ref(null);
const isClickOpenSaleProductBtn = ref(false);
const clickedProductModalData = ref();
const hasClickedProductVariants = ref(true);
const selectedShipmentTemplate = ref({});
const selectedVariants = ref([]);
const addCatalogModalSelectableAreaRef = ref();
const bulkOperationsRef = ref();
const catalogTableRef = ref();
const isOpenSaleProductBtnDisabled = ref(false);
const successProcessProductImg = ref();
const isSkuExist = ref(false);
const pimsId = ref(null);
const selectedProductCategoryId = ref(null);

const getCatalogModalDeleteRowId = computed(
  () => store.getters["navigation/getCatalogModalDeleteRowId"]
);
const clickedProductModalImagesTransform = computed(() => {
  return clickedProductModalImages.value.map((item) => ({
    Url: item?.imageUrl?.replace("/org/", "/261_371/"),
  }));
});

const getSelectedRowsLength = computed(
  () => Object.keys(catalogTableRef.value?.getSelectedRows() ?? []).length
);

const applySelected = () => {
  const ids = Object.keys(catalogTableRef.value?.getSelectedRows() ?? []);
  ids.forEach((rowId) => {
    let itemIndex = selectedVariants.value?.findIndex(
      (item) => item.id === Number(rowId)
    );
    if (itemIndex !== -1 && itemIndex !== undefined) {
      const bulkData = bulkOperationsRef.value?.bulkOperationsData;
      const selectedData = selectedVariants.value[itemIndex];
      const fieldsToUpdate = [
        "listPrice",
        "salePrice",
        "stock",
        "duration",
        "vatRate",
      ];

      fieldsToUpdate.forEach((field) => {
        const bulkValue = bulkData?.[field];
        const selectedValue = selectedData?.[field];

        if (
          bulkValue !== undefined &&
          bulkValue !== selectedValue &&
          bulkValue !== 0 &&
          bulkValue !== "" &&
          bulkValue !== null
        ) {
          catalogTableRef.value?.table?.options?.meta.updateRow(
            Number(rowId),
            field,
            bulkValue
          );
        }
      });
    }
  });

  catalogTableRef.value.table.resetRowSelection();
};

const goToProductPage = () => {
  sendProductLinkClickEvent();

  const currentHostname = window.location.hostname;
  const baseUrl = HOST_NAMES[currentHostname] ?? "https://www.n11.com";

  const catalogUrl = `${baseUrl}/urun/catalog-`;
  const url = `${catalogUrl}${selectedProduct.value?.productGroupId}`;

  window.open(url, "_blank");
};

const createEmptyValidation = () => ({
  isDirty: false,
  error: false,
  message: null,
});

const createEmptyVariant = (pimsId, gtin, categoryId) => ({
  id: pimsId,
  name: "",
  gtin,
  categoryId,
  stockCode: null,
  listPrice: null,
  salePrice: null,
  stock: null,
  duration: null,
  validations: {
    stockCode: createEmptyValidation(),
    listPrice: createEmptyValidation(),
    salePrice: createEmptyValidation(),
    stock: createEmptyValidation(),
    duration: createEmptyValidation(),
    vatRate: createEmptyValidation(),
  },
});

const handleNoVariants = async (productList) => {
  hasClickedProductVariants.value = false;
  const firstProduct = productList?.[0];
  isSkuExist.value = firstProduct.isSkuExist;
  if (firstProduct) {
    selectedVariants.value = [
      createEmptyVariant(
        firstProduct.pimsId,
        firstProduct.gtin,
        firstProduct.categoryId
      ),
    ];
  }
};

const handleWithVariants = async (productList) => {
  isSkuExist.value = false;
  const isSkuExistValue = productList.find(
    (product) => product.pimsId === selectedProduct.value?.pimsId
  )?.isSkuExist;
  await Promise.resolve();
  if (
    addCatalogModalSelectableAreaRef.value &&
    selectedProduct.value?.pimsId &&
    !isSkuExistValue
  ) {
    addCatalogModalSelectableAreaRef.value.addSelectedItemsValue(
      selectedProduct.value.pimsId
    );
  }
};

const getClickedProductInfo = async (pimsId, groupId, item) => {
  emit("loadingClickedProductId", pimsId);
  selectedProductCategoryId.value = item.categoryId;
  store.commit("categories/SET_CATEGORY_ID", item.categoryId);

  try {
    const res = await store.dispatch("products/getProductInfo", { groupId });
    clickedProductModalData.value = res;
    catalogModalStyle.value = { display: "block" };

    const hasVariants = res?.variantList?.length > 0;
    const { productList } = res;

    const productListWithCategory = productList.map((product) => ({
      ...product,
      categoryId: item.categoryId,
    }));

    if (hasVariants) {
      await handleWithVariants(productListWithCategory);
    } else {
      await handleNoVariants(productListWithCategory);
    }
  } catch (error) {
    console.error(error);
    addCatalogModalRef.value?.hiddenModal();
  } finally {
    emit("loadingClickedProductId", false);
  }
};

const imageUrl = "https://n11scdn.akamaized.net/custom/upload/64/53/3650777660751629915.svg";
const getClickedProductModalImages = async (item, pimsId, groupId) => {
  try {
    const { data } = await store.dispatch("products/getProductImages", pimsId);
    clickedProductModalImages.value = data?.length ? data : [{ imageUrl }];
  } catch (error) {
    console.error(error);
    clickedProductModalImages.value = [{ imageUrl }];
  } finally {
    selectedProduct.value = {
      ...item,
      attributes: [
        { Key: "Kategori", Value: item.categoryName },
        { Key: "Marka", Value: item.brandName },
        { Key: "Renk", Value: item.color },
      ],
    };
    addCatalogModalRef.value.openModal();
    catalogModalStyle.value = { display: "none" };
  }
  await getClickedProductInfo(pimsId, groupId, item);
};

const isOpen = async (item) => {
  await getClickedProductModalImages(item, item.pimsId, item.productGroupId);
  if (isSkuExist.value) {
    pimsId.value = item.pimsId;
    emit("openListedProductWarningModal", pimsId);
    addCatalogModalRef.value.hiddenModal();
  }
};

const clickedOpenSaleProductBtn = async () => {
  isClickOpenSaleProductBtn.value = true;
  isOpenSaleProductBtnDisabled.value = true;

  let isValid = false;

  const getTableData = catalogTableRef.value?.table?.options?.meta?.getData();
  isValid = !!getTableData.length; // check if any variants

  if (isValid) {
    isValid &= !!selectedShipmentTemplate.value?.id; // check if shipment template
    getTableData.map((field) => {
      const valid = Object.keys(field.validations).map((fieldName) => {
        const validated = validate(
          field[fieldName],
          fieldName,
          field,
          true,
          catalogTableRef.value?.table?.options?.meta?.getColumnDataExceptThis(
            fieldName,
            field.id
          )
        );

        return validated;
      });

      isValid &= valid.every((val) => val === true);
    });
  }

  if (isValid) {
    const sanitizedTableData = JSON.parse(JSON.stringify(getTableData));
    const productDetails = sanitizedTableData.map((b) => {
      b.pimsId = b.id;
      b.preparingTime = b.duration;
      b.vatRate = Number(b.vatRate);

      delete b.id;
      delete b.duration;
      delete b.validations;

      return b;
    });

    try {
      const res = await store.dispatch("products/openForSale", {
        productGroupId: selectedProduct?.value?.productGroupId,
        shipmentTemplate: selectedShipmentTemplate.value.name,
        productDetails,
      });

      let successCount = res.data.successCount;
      if (successCount > 0) {
        sendOpenProductForSaleEvent(successCount);
      }

      if (res.data.failCount === 0) {
        addCatalogModalRef.value.hiddenModal();

        emit("setIsNotificationShow", true);
        setTimeout(() => {
          emit("setIsNotificationShow", false);
        }, 10000);

        const selectedRowItem = props.paginatedRows.find(
          (row) => row.id === selectedProduct?.value?.productGroupId
        );
        successProcessProductImg.value = selectedRowItem.original.imageUrl;
      } else {
        store.commit("common/SET_TOASTER_DATA", {
          type: "error",
          title: "",
          description:
            res.data.errorDetails[Object.keys(res.data.errorDetails)[0]],
        });
      }
    } catch (err) {
      store.commit("common/SET_TOASTER_DATA", {
        type: "error",
        title: "",
        description: err.errorDetails[Object.keys(err.errorDetails)[0]],
      });
    } finally {
      isOpenSaleProductBtnDisabled.value = false;
    }
  } else {
    setTimeout(() => {
      isOpenSaleProductBtnDisabled.value = false;
    }, 150);
  }
};

const addProductModalClosed = () => {
  selectedVariants.value = [];
  selectedShipmentTemplate.value = {};
  hasClickedProductVariants.value = true;
  isClickOpenSaleProductBtn.value = false;
  catalogModalStyle.value = { display: "none" };

  if (bulkOperationsRef && bulkOperationsRef.value) {
    bulkOperationsRef.value.bulkOperationsData = {
      listPrice: null,
      salePrice: null,
      stock: null,
      duration: null,
    };
  }
};

watch(
  () => selectedVariants.value,
  (newSelectedVariants, oldSelectedVariants) => {
    if (
      newSelectedVariants &&
      oldSelectedVariants &&
      newSelectedVariants.length != oldSelectedVariants.length
    ) {
      const addedVariants = newSelectedVariants?.filter(
        (obj2) => !oldSelectedVariants?.some((obj1) => obj1.id === obj2.id)
      );
      const removedVariants = oldSelectedVariants?.filter(
        (obj1) => !newSelectedVariants?.some((obj2) => obj2.id === obj1.id)
      );

      if (addedVariants.length) {
        addedVariants.map((added) => {
          catalogTableRef.value?.table?.options?.meta.addRow(added);
        });
      }

      if (removedVariants.length) {
        removedVariants.map((removed) => {
          catalogTableRef.value?.table?.options?.meta.removeRow(removed.id);
        });
      }
    }
  }
);

watch(getCatalogModalDeleteRowId, (newDeleteRowId) => {
  if (newDeleteRowId) {
    catalogTableRef.value?.table?.options?.meta.removeRow(newDeleteRowId);
  }
});

defineExpose({
  isOpen,
  successProcessProductImg,
});
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";

.catalog-product-modal {
  display: flex;
  gap: 16px;
  color: $colorN80;
  text-align: left;

  .catalog-product-main-wrapper {
    position: relative;
    width: 100%;
    padding-bottom: 64px;
  }

  .modal-product-title {
    display: inline-block;
    font-size: 20px;
    font-weight: 600;
    letter-spacing: -0.02px;
    line-height: 26px;
    margin-bottom: 24px;
    cursor: pointer;
    text-decoration: underline;
  }

  .modal-bottom {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    position: absolute;
    bottom: 0;
    width: 100%;

    .modal-bottom-alert {
      font-size: 13px;
      letter-spacing: -0.05px;
      width: 100%;
      padding: 12px 16px;
      display: flex;
      align-items: center;
      border-radius: 6px;
      gap: 12px;
      background-color: $colorB05;

      img {
        width: 24px;
        height: 24px;
      }
    }

    .modal-bottom-text {
      b {
        font-weight: 700;
      }
    }
  }
}
</style>
